import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import SimpleBar from 'simplebar-react';

import dayjs, { Dayjs } from 'dayjs';

import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import DatePickerCustom from 'src/components/common/field/DatePickerCustom';
import DropDown from 'src/components/common/field/DropDown';
import DropDownMultiSelect from 'src/components/common/field/DropDownMultiSelect';
import InputSearchCustom from 'src/components/common/field/InputSearchCustom';
import InputTextAreaAutosize from 'src/components/common/field/InputTextAreaAutosize';
import TimePickerCustom from 'src/components/common/field/TimePickerCustom';

import { getField, isEmpty } from 'src/common/helper';
import { BaseDataType } from 'src/components/reservationList/TypeCustom';
import { useNotify } from 'src/context/ContextNotify';
import { postAPI } from 'src/utils/api';
import { getToken, getUserLogged } from 'src/utils/auth';

const typographyLabel = {
  fontSize: '14px',
  lineHeight: '16.8px',
  fontWeight: 500,
  width: '90px'
};

const inputSX = {
  width: '160px',
  height: '48px'
};

const sxLableDropDown = {
  color: '#434154',
  '&.Mui-focused': { color: '#434154', transform: 'translate(14px, 16px) scale(1)' },
  '&.MuiInputLabel-root': { color: '#434154', transform: 'translate(14px, 16px) scale(1)' }
}

const StackCenter = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
});

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500'
};

const currentDate: string = dayjs().format('YYYY-MM-DD');

const defaultForm = {
  id: null,
  doctorid: null,
  typeid: null,
  thedate: currentDate,
  starttime: '12:00',
  finishtime: '12:30',
  visittime: null,
  fullname: '',
  patientid: '',
  patienthistory: '',
  patient_note: '',
  partcategories: 0,
  opcategoryid: 1,
  partid: [],
  status: null,
  person: null,
  note: '',
  caseid: 0,
  quoteid: 0
};

const defaultError = {
  doctorid: true,
  typeid: true,
  thedate: true,
  starttime: true,
  finishtime: true,
  person: true
};

type DataFormType = {
  id: number | null;
  doctorid: number | null;
  typeid: number | null;
  thedate: string | null;
  starttime: string | null;
  finishtime: string | null;
  visittime: string | null;
  fullname: string;
  patientid: string;
  patienthistory: string;
  patient_note: string;
  partcategories: number | null;
  opcategoryid: number | null;
  partid: number[];
  status: number | null;
  person: number | null;
  note: string;
  caseid: number | null;
  quoteid: number | null;
};

interface DialogFormProps {
  open: boolean;
  data: any | undefined;
  baseData: BaseDataType | undefined;
  onClose: (isRefreshList?: boolean) => void;
  onSave?: () => void;
};

const DialogForm: React.FC<DialogFormProps> = (props) => {
  const notifyContext = useNotify();
  const [dataForm, setDataForm] = useState<DataFormType>(defaultForm);
  const [errors, setErrors] = useState(defaultError);

  const handleClose = () => {
    props.onClose();
  }

  const handleChange = (name: string) => (value: number | string | number[] | null) => {
    setDataForm(prevState => ({
      ...prevState,
      [name]: value
    }))
  };

  const handleChangeDropdownMulti = (itemIds: number[]) => {
    handleChange('partid')(itemIds);
  };

  const handleChangeDate = (date: Dayjs | null) => {
    const dateFormat = date ? date.format('YYYY/MM/DD') : null;
    handleChange('thedate')(dateFormat);
  };

  const handleChangeTime = (name: string) => (date: Dayjs | null) => {
    const timeFormat = date ? date.format('HH:mm') : null;

    if (name === 'starttime' && timeFormat) {
      const finishTime = date ? date.add(30, 'minutes').format('HH:mm') : null;
      handleChange('finishtime')(finishTime);
    }

    handleChange(name)(timeFormat);
  };

  const handleChangeInputSearch = (value: any | undefined) => {
    handleChange('patientid')(getField(value, 'patientno', ''));
  };

  const validate = () => {
    if (isEmpty(dataForm.doctorid)) {
      setErrors({ ...defaultError, doctorid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '医師を選択してください',
        type: 'error'
      });
      return false;
    }
    if (isEmpty(dataForm.typeid)) {
      setErrors({ ...defaultError, typeid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '予約タイプを選択してください',
        type: 'error'
      });
      return false;
    }
    if (isEmpty(dataForm.thedate)) {
      setErrors({ ...defaultError, thedate: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '日付を選択してください',
        type: 'error'
      })
      return false;
    }
    if (isEmpty(dataForm.starttime)) {
      setErrors({ ...defaultError, starttime: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '開始時間を選択してください',
        type: 'error'
      })
      return false;
    }
    if (isEmpty(dataForm.finishtime)) {
      setErrors({ ...defaultError, finishtime: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '終了時間を選択してください',
        type: 'error'
      })
      return false;
    }
    if (isEmpty(dataForm.person)) {
      setErrors({ ...defaultError, person: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '担当者をお選びください',
        type: 'error'
      });
      return false;
    }

    setErrors(defaultError);

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error'
    });

    return true;
  };

  const handleButtonClick = () => {
    if (!validate()) {
      return;
    }
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      const data = dataForm;

      postAPI(`/reserve/save?&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            props.onClose(true);
          }
          if (res.code === 501 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error'
            });
          }
        })
        .finally(() => {
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  }

  const setSelectDefault = () => {
    const status = props.baseData?.status

    if (status) {
      const statusSelect = status.find(item => item.name === '仮予約');

      setDataForm(prevState => ({
        ...prevState,
        status: statusSelect ? Number(statusSelect.id) : null
      }));
    }

    const userLogin = getUserLogged();
    if (userLogin && userLogin.user && userLogin.user.id) {
      setDataForm(prevState => ({
        ...prevState,
        person: userLogin.user.id
      }));
    }
  };

  const setSelectEdit = () => {
    setDataForm(prevState => ({
      ...prevState,
      id: getField(props.data, 'id', null),
      doctorid: getField(props.data, 'doctorid', null),
      typeid: getField(props.data, 'typeid', null),
      thedate: getField(props.data, 'thedate', null),
      starttime: getField(props.data, 'starttime', null),
      finishtime: getField(props.data, 'finishtime', null),
      visittime: getField(props.data, 'visittime', null),
      fullname: getField(props.data, 'patientname', ''),
      patientid: getField(props.data, 'patientid', ''),
      partid: getField(props.data, 'partid', null),
      status: getField(props.data, 'status', null),
      person: getField(props.data, 'person', null),
      note: getField(props.data, 'note', ''),
      caseid: getField(props.data, 'caseid', 0),
      quoteid: getField(props.data, 'quoteid', 0)
    }));
  }

  const initData = () => {
    if (props.data) {
      setSelectEdit();
    } else {
      setSelectDefault();
    }
  };

  useEffect(() => {
    if (props.open) {
      initData();
    } else {
      setDataForm(defaultForm);
      setErrors(defaultError);
    }
    // eslint-disable-next-line
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '488px',
          m: '0px'
        }
      }}>
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '0px' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 500 }}>
            新規予約
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          overflow: 'hidden',
          p: '0px 10px 24px 24px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 210px)', borderWidth: '18px' }}
        >
          <Stack spacing='10px'>
            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                医師
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              <DropDown
                selected={dataForm.doctorid}
                idSelect='doctor-select'
                placeholder='選択してください'
                items={props.baseData?.doctors}
                onChange={handleChange('doctorid')}
                sxInput={{ ...inputSX }}
                sxLabel={sxLableDropDown}
                error={!errors.doctorid}
              />
            </StackCenter>

            <StackCenter >
              <Typography sx={{ ...typographyLabel }}>
                予約タイプ
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              <DropDown
                selected={dataForm.typeid}
                idSelect='typeid-select'
                placeholder='選択してください'
                items={props.baseData?.ops}
                onChange={handleChange('typeid')}
                sxInput={{ ...inputSX }}
                sxLabel={sxLableDropDown}
                error={!errors.typeid}
              />
            </StackCenter>

            <StackCenter >

              <Typography sx={{ ...typographyLabel }}>
                予約日
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              <DatePickerCustom
                value={dayjs(dataForm.thedate)}
                onChange={handleChangeDate}
                error={!errors.thedate}
              />
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                時間
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              <StackCenter justifyContent='left'>
                <TimePickerCustom
                  value={dayjs(dataForm.thedate
                    ? dataForm.thedate + ' ' + dataForm.starttime
                    : currentDate + ' ' + dataForm.starttime
                  )}
                  onChange={handleChangeTime('starttime')}
                  error={!errors.starttime}
                />
                <Typography sx={{ ...typographyLabel, display: 'flex', alignItems: 'center', mx: '5px', width: 'unset' }}>〜</Typography>
                <TimePickerCustom
                  value={dayjs(dataForm.thedate
                    ? dataForm.thedate + ' ' + dataForm.finishtime
                    : currentDate + ' ' + dataForm.finishtime)}
                  onChange={handleChangeTime('finishtime')}
                  error={!errors.finishtime}
                />
              </StackCenter>
            </StackCenter>

            <StackCenter >
              <Typography sx={{ ...typographyLabel }}>
                来院時間
              </Typography>
              <TimePickerCustom value={dayjs(dataForm.thedate + ' ' + dataForm.visittime)} onChange={handleChangeTime('visittime')} />
            </StackCenter>

            <StackCenter>
              <Stack>
                <Typography sx={{ ...typographyLabel }}>
                  患者情報
                </Typography>
                <Typography sx={{ ...typographyLabel, fontSize: '12px', fontWeight: 400 }}>
                  (どちらか)
                </Typography>
              </Stack>
              <StackCenter>
                <InputSearchCustom
                  labelInput='患者名'
                  name='fullname'
                  value={dataForm.fullname}
                  onChange={handleChangeInputSearch}
                  sx={{ mr: '10px' }}
                  readonly={props.data ? true : false}
                />
                <InputSearchCustom
                  labelInput='患者ID'
                  name='patientid'
                  value={dataForm.patientid}
                  readonly={true}
                />
              </StackCenter>
            </StackCenter>

            <StackCenter >
              <Typography sx={{ ...typographyLabel }}>
                希望施術
              </Typography>
              <Stack spacing={'10px'}>
                <DropDown
                  selected={dataForm.partcategories}
                  idSelect='part-category-select'
                  placeholder='選択してください'
                  items={props.baseData?.part_categories}
                  onChange={handleChange('partcategories')}
                  sxInput={{ ...inputSX, width: '344px' }}
                  sxLabel={sxLableDropDown}
                />
                <DropDownMultiSelect
                  selected={dataForm.partid}
                  idSelect='parts-select'
                  placeholder='選択してください'
                  items={props.baseData?.parts}
                  parentId={dataForm.partcategories}
                  onChange={handleChangeDropdownMulti}
                  sxInput={{ ...inputSX, width: '344px' }}
                  sxLabel={sxLableDropDown}
                />
              </Stack>
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                ステータス
              </Typography>
              <DropDown
                selected={dataForm.status}
                idSelect='status-select'
                placeholder='選択してください'
                items={props.baseData?.status}
                onChange={handleChange('status')}
                sxInput={{ ...inputSX }}
                sxLabel={sxLableDropDown}
              />
            </StackCenter>

            <StackCenter >
              <Typography sx={{ ...typographyLabel }}>
                担当者
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              <DropDown
                selected={dataForm.person}
                idSelect='user-select'
                placeholder='選択してください'
                items={props.baseData?.users}
                onChange={handleChange('person')}
                sxInput={{ ...inputSX }}
                sxLabel={sxLableDropDown}
                error={!errors.person}
              />
            </StackCenter>

            <StackCenter >
              <Typography sx={{ ...typographyLabel }}>
                備考
              </Typography>
              <InputTextAreaAutosize
                value={dataForm.note}
                onChange={handleChange('note')}
                width='344px'
                height='104px'
              />
            </StackCenter>
          </Stack>
        </SimpleBar>
      </DialogContent>
      <DialogActions
        style={{
          justifyContent: 'right',
          padding: '16px'
        }}
      >
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} />
        <BtnGradient text='登録' handleButtonClick={handleButtonClick} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogForm;